import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Box } from '@/UI/Box/Box';
import { TourStepMediaType } from '@/enums/tour-step-media-type.enum';
import { log } from '@/logger/logger.ts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { NEW_CANVAS_FTUE_EVENT_NAMES } from '@/consts/analytics.consts';
import { useWorkflowStore } from '@/state/workflow.state';
import { ZIndex } from '@/consts/z-indexes.consts';
import { LottieAnimation } from '../Common/LottieAnimation';
import { MeetWeaveModal } from '../WelcomeModal/MeetWeaveModal';
import { useTour } from './TourContext';
import { useRegisteredTourAnchor } from './tourAnchors.store';
import { useToolBuilderStepDismiss } from './useToolBuilderStepDismiss';
import { StartOfTourModal } from './Components/StartOfTourModal';
import { ToolBuilderIntroModal } from './Components/ToolBuilderIntroModal';
import { ToolBuilderTourCoachmark } from './Components/ToolBuilderTourCoachmark';
import { TourCircleHighlight } from './Components/TourCircleHighlight';
import { TourModal } from './Components/TourModal';
import { TourTooltip } from './Components/TourTooltip';
import './tour.css';

const logger = log.getLogger('Tour');

const SUCCESS_ANIMATION_SRC = '/tour-assets/animations/tour-circle-check-success.lottie';

function resolveTourAnchor(elementId: string | undefined): HTMLElement | null {
  if (!elementId) return null;
  return document.getElementById(elementId) || document.querySelector<HTMLElement>(`[data-tour-anchor="${elementId}"]`);
}

const preloadMedia = (imageUrl: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = img.onabort = function () {
      reject(new Error(`could not load image: ${imageUrl}`));
    };
    img.src = imageUrl.startsWith('http') ? imageUrl : `${window.location.origin}${imageUrl}`;
  });
};

export const Tour = () => {
  const { steps } = useTour();

  useEffect(() => {
    if (!steps.length) return;

    steps.map((step) => {
      if (step.media) {
        const allMedia = [step.media.default, ...Object.values(step.media)];
        allMedia.forEach((media) => {
          if ([TourStepMediaType.IMAGE, TourStepMediaType.SVG].includes(media.type)) {
            void preloadMedia(media.path);
          }
        });
      }
      if (Array.isArray(step.cta) && step.cta.length > 0) {
        step.cta.forEach((cta) => {
          if (cta.media) {
            const allMedia = [cta.media.default, ...Object.values(cta.media)];
            allMedia.forEach((media) => {
              if ([TourStepMediaType.IMAGE, TourStepMediaType.SVG].includes(media.type)) {
                void preloadMedia(media.path);
              }
            });
          }
        });
      }
    });
  }, [steps]);

  return <TourComponent />;
};

const SuccessAnimation = () => {
  const { showAnimation, onSuccessAnimationEnd } = useTour();

  return createPortal(
    <Box
      className="success-animation"
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence>
        {showAnimation ? (
          <motion.div
            initial={{ opacity: 1, translateY: 0 }}
            animate={{ opacity: [1, 1, 1, 0], translateY: [0, 0, -20, -64] }}
            transition={{ duration: 1.3, ease: 'easeInOut', times: [0, 0.7, 0.8, 1] }}
            onAnimationComplete={onSuccessAnimationEnd}
          >
            <LottieAnimation width={64} height={64} loop={false} src={SUCCESS_ANIMATION_SRC} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Box>,
    document.body,
  );
};

const TourComponent = () => {
  const { callAction, steps, currentStep, endTour, goToNextStep, hiddenTooltipStepId, stopTour } = useTour();
  const { track } = useAnalytics();
  const workflowId = useWorkflowStore((s) => s.recipe.id);
  const toolBuilderTour = useToolBuilderStepDismiss();
  const { t } = useTranslation();
  const step = steps[currentStep];
  const isToolBuilderCoachmark = step?.type === 'tooltip' && step.tooltipVariant === 'tool-builder';
  const mediaSrc = isToolBuilderCoachmark ? step.media?.default.path : undefined;
  const registeredAnchor = useRegisteredTourAnchor(isToolBuilderCoachmark ? step.element : undefined);

  useEffect(() => {
    if (!isToolBuilderCoachmark || mediaSrc) return;
    logger.error('Tool-builder coachmark is missing media', { currentStep, stepId: step?.stepId });
    stopTour();
  }, [currentStep, isToolBuilderCoachmark, mediaSrc, step?.stepId, stopTour]);

  if (!steps.length) return null;

  const renderStepContent = () => {
    if (!step) {
      logger.error('No step found', { steps, currentStep });
      return null;
    }

    switch (step.type) {
      case 'tooltip': {
        if (step.stepId === hiddenTooltipStepId) return null;

        // A registered anchor re-renders this component when it mounts, which a DOM lookup here
        // cannot; the lookup stays as the fallback for steps whose anchor does not register.
        const element = registeredAnchor ?? resolveTourAnchor(step.element);

        if (step.tooltipVariant === 'tool-builder') {
          // Coachmarks ignore outside clicks, so never mount one without an anchor — e.g. while
          // Version History replaces the panel.
          if (!mediaSrc || !element) return null;
          return (
            <ToolBuilderTourCoachmark
              actionLabel={step.cta?.[0] ? t(step.cta[0].label) : undefined}
              anchorEl={element}
              content={t(step.content)}
              mediaCrop={step.mediaCrop}
              mediaSrc={mediaSrc}
              onAbandon={toolBuilderTour.abandonTour}
              onDismiss={() => {
                const action = step.cta?.[0];
                if (action?.hook) void callAction(action);
                toolBuilderTour.dismissStep(currentStep);
              }}
              open
              title={t(step.title)}
            />
          );
        }

        return (
          <>
            {element && step.highlight === 'circle' ? <TourCircleHighlight anchorEl={element} /> : null}
            <TourTooltip
              actions={step.cta}
              anchorEl={element}
              content={t(step.content)}
              media={step.media}
              placement={step.placement || 'bottom'}
              skidding={step.skidding}
              offset={step.offset}
              title={t(step.title)}
            />
          </>
        );
      }
      case 'modal':
        switch (step.modalVariant) {
          case 'start-of-tour':
            return (
              <StartOfTourModal
                content={t(step.content)}
                disallowClose={step.disallowClose}
                media={step.media}
                onClose={stopTour}
                onNextStep={goToNextStep}
                open
                title={t(step.title)}
              />
            );
          case 'welcome':
            return (
              <MeetWeaveModal
                open
                title={t(step.title)}
                body={t(step.content)}
                disallowClose={step.disallowClose}
                onClose={stopTour}
                onNextStep={() => {
                  track(NEW_CANVAS_FTUE_EVENT_NAMES.WELCOME_GET_STARTED_CLICKED, { workflowId }, TrackTypeEnum.BI);
                  void goToNextStep();
                }}
              />
            );
          case 'tool-builder':
            return (
              <ToolBuilderIntroModal
                actions={step.cta}
                content={t(step.content)}
                onClose={toolBuilderTour.abandonTour}
                open
                title={t(step.title)}
              />
            );
          default:
            return (
              <TourModal
                actions={step.cta}
                content={t(step.content)}
                disallowClose={step.disallowClose}
                media={step.media}
                onClose={endTour}
                open
                title={t(step.title)}
              />
            );
        }
      default:
        return null;
    }
  };

  return (
    <Box id="tour-box" sx={{ position: 'fixed', zIndex: ZIndex.Overlays.PRODUCT_TOUR_OVERLAY }}>
      {renderStepContent()}
      <SuccessAnimation />
    </Box>
  );
};
